import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { UsersRound, Plus, Pencil, Trash2, Shield, Store, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLES = [
  { v: "super_admin", label: "Super Admin", icon: Shield, color: "bg-accent/10 text-accent border-accent/20", desc: "Akses penuh ke semua fitur & semua bisnis" },
  { v: "business_admin", label: "Admin Bisnis", icon: Store, color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", desc: "Kelola bisnis yang ditugaskan (tanpa kelola user & database)" },
  { v: "operator", label: "Operator", icon: Eye, color: "bg-amber-500/10 text-amber-600 border-amber-500/20", desc: "Hanya lihat leads & riwayat percakapan" },
];
const roleMeta = (v) => ROLES.find((r) => r.v === v) || ROLES[1];

const EMPTY = { name: "", username: "", email: "", password: "", role: "business_admin", business_ids: [] };

export default function UserManager({ businesses }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", username: u.username || "", email: u.email || "", password: "", role: u.role, business_ids: u.business_ids || [] });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBiz = (id) =>
    setForm((f) => ({ ...f, business_ids: f.business_ids.includes(id) ? f.business_ids.filter((x) => x !== id) : [...f.business_ids, id] }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, username: form.username, role: form.role, business_ids: form.business_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("User dibuat");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Kelola akun & hak akses (privilege) tiap user.</p>
        <Button data-testid="btn-add-user" onClick={openNew} className="bg-accent hover:bg-accent/90 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Tambah User
        </Button>
      </div>

      <div className="space-y-2" data-testid="user-list">
        {users.map((u) => {
          const m = roleMeta(u.role);
          return (
            <div key={u.id} data-testid="user-item"
              className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold shrink-0">
                {(u.name || "U")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{u.name}</p>
                  {u.is_primary && <Badge variant="outline" className="text-[10px]">Utama</Badge>}
                </div>
                <p className="text-[11px] text-muted-foreground truncate">
                  {u.username ? `@${u.username} · ` : ""}{u.email}
                  {u.role !== "super_admin" && ` · ${u.business_ids.length} bisnis`}
                </p>
              </div>
              <Badge variant="outline" className={`${m.color} shrink-0`}>
                <m.icon className="w-3.5 h-3.5 mr-1" /> {m.label}
              </Badge>
              <Button size="icon" variant="ghost" data-testid={`btn-edit-user-${u.id}`} onClick={() => openEdit(u)}>
                <Pencil className="w-4 h-4" />
              </Button>
              {!u.is_primary && (
                <Button size="icon" variant="ghost" data-testid={`btn-delete-user-${u.id}`}
                  onClick={() => remove(u)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Tambah User Baru"}</DialogTitle>
            <DialogDescription>Atur data akun dan hak akses (privilege) user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Lengkap</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Username</Label>
                <Input data-testid="user-username" value={form.username} placeholder="opsional"
                  onChange={(e) => set("username", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input data-testid="user-email" type="email" value={form.email} disabled={!!editing}
                  onChange={(e) => set("email", e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password"}</Label>
              <Input data-testid="user-password" type="password" value={form.password}
                placeholder={editing ? "••••••••" : "Minimal 6 karakter"}
                onChange={(e) => set("password", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hak Akses (Privilege)</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{roleMeta(form.role).desc}</p>
            </div>
            {form.role !== "super_admin" && (
              <div className="space-y-2">
                <Label>Bisnis yang Dapat Diakses</Label>
                <div className="space-y-1.5 max-h-44 overflow-y-auto rounded-lg border border-border p-3" data-testid="user-business-list">
                  {businesses.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox data-testid={`user-biz-${b.id}`} checked={form.business_ids.includes(b.id)}
                        onCheckedChange={() => toggleBiz(b.id)} />
                      <span className="w-2 h-2 rounded-full" style={{ background: b.config.accent_color }} />
                      {b.name}
                    </label>
                  ))}
                  {businesses.length === 0 && <p className="text-xs text-muted-foreground">Belum ada bisnis.</p>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="btn-save-user" onClick={save} disabled={saving}
              className="bg-accent hover:bg-accent/90 text-white">
              {saving ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Buat User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
